<template>
  <BackPageLayout topbar-color="white" color="white">
    <template #leadingicon>
      <ArrowLeftIcon class="h-6 w-6 cursor-pointer" @click="router.push({
        name: backPage
      })" />
    </template>

    <template #headline>
      {{ $t('newcomponent.headline') }}
    </template>

    <div class="px-2 mx-auto flex justify-center items-center flex-1 mt-2 mb-8">
      <div v-for="path, n in paths  " :key="n"
        class="items-center justify-center"
        :class="n === paths.length - 1 ? 'shrink' : 'grow'">
        <router-link :to="{ name: path.name }" v-slot="{ isExactActive }"
          class="flex items-center text-center transition-all" :class="{
            'pointer-events-none': path.position >= currentPath?.position,
          }">

          <div
            class="rounded-full h-10 w-10 border-2 flex items-center justify-center transition-all"
            :class="{
              'border-apex-blue-500 bg-white': isExactActive,
              'border-apex-blue-500 bg-apex-blue-500': path.position < currentPath?.position
            }">
            <Transition name="fade">
              <CheckIcon v-if="path.position < currentPath?.position"
                class="h-5 w-5 text-white stroke-2" />

              <div v-else class="text-sm"
                :class="{ 'text-apex-blue-500 font-bold': isExactActive }">
                {{ path.positionLabel }}
              </div>
            </Transition>
          </div>

          <div class="h-0.5 w-auto grow transition-all" :class="{
            'hidden': n === paths.length - 1,
            'bg-gray-300': path.position >= currentPath?.position,
            'bg-apex-blue-500': path.position < currentPath?.position
          }"></div>

        </router-link>
      </div>
    </div>

    <div class="relative pb-24">
      <router-view v-slot="{ Component }">
        <Transition :name="transition">
          <component :is="Component" />
        </Transition>
      </router-view>
    </div>


    <template #footer>
      <OutlinedButton :to="{ name: currentPath?.previous ?? 'Dashboard' }">
        <span v-if="currentPath?.previous">{{ $t('Previous')
          }}</span>
        <span v-else>{{ $t('Cancel') }}</span>
      </OutlinedButton>

      <ElevatedButton @click="handleNext"
        :status="sending ? 'loading' : 'default'">
        <span v-if="currentPath?.next">{{ $t('Next') }}</span>
        <span v-else>{{ $t('Finish') }}</span>
      </ElevatedButton>


    </template>
  </BackPageLayout>
</template>
<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/vue/24/outline';
import BackPageLayout from '../../layouts/BackPageLayout.vue';
import { RouteRecordName, useRouter } from 'vue-router';
import { useNewComponentStore } from '../../stores/newComponentStore';
import OutlinedButton from '../../components/buttons/OutlinedButton.vue';
import ElevatedButton from '../../components/buttons/ElevatedButton.vue';
import { useAppSettingsStore } from '../../stores/AppSettingsStore';
const router = useRouter();
const newComponentsStore = useNewComponentStore();
const { t } = useI18n();
const transition = ref('')


interface Route {
  position: Number;
  label: String;
  positionLabel: String;
  name: RouteRecordName;
  next?: RouteRecordName;
  previous?: RouteRecordName;

}

const appStore = useAppSettingsStore();

const currentPath = computed(() => {
  return paths.value.find((path) => path.name === router.currentRoute.value.name) ?? paths.value[0];
});

const paths = computed<Route[]>(() => [
  {
    position: 0,
    positionLabel: "01",
    label: t('newcomponent.steps.period'),
    name: 'NewComponentType',
    next: 'NewComponentDates',

  },
  {
    position: 1,
    label: t('newcomponent.steps.dates'),
    positionLabel: "02",
    name: 'NewComponentDates',
    next: 'NewComponentDatasets',
    previous: 'NewComponentType'
  },
  {
    position: 2,
    label: t('newcomponent.steps.data_choice'),
    positionLabel: "03",
    name: 'NewComponentDatasets',
    next: 'NewComponentChart',
    previous: 'NewComponentDates'
  },
  {
    position: 3,
    label: t('newcomponent.steps.charts'),
    positionLabel: "04",
    name: 'NewComponentChart',
    previous: 'NewComponentDatasets'
  }
]);

const backPage = ref(t('common.dashboard_title'));

watch(router.currentRoute, (to, from) => {
  const toName = to.name as string;
  const fromName = from.name as string;

  const toPosition = paths.value.find((path) => path.name === toName)?.position;
  const fromPosition = paths.value.find((path) => path.name === fromName)?.position;

  if (toPosition === undefined || fromPosition === undefined) {
    return;
  }

  transition.value = toPosition > fromPosition ? 'tab-slide-left' : 'tab-slide-right';
});

const sending = ref(false);

const handleNext = async () => {
  if (currentPath.value.next) {
    router.push({ name: currentPath.value.next });
  } else {
    // Send data
    await appStore.startLoadingPage(5);
    sending.value = true;
    const success = await newComponentsStore.send();
    appStore.advance();
    sending.value = false

    if (success) {
      router.push({
        name: 'Dashboard',
        query: { refresh: 'true' }
      });
    }
  }
}

onMounted(() => {
  const componentId = router.currentRoute.value.params.id as string;

  if (componentId) {
    newComponentsStore.loadEditing(componentId);
    backPage.value = t('common.components_title')
    // Set meta transition
  }
})

onUnmounted(() => {
  newComponentsStore.reset();
})
</script>